import type { ReactNode } from 'react';
import type { BedLayout } from '@lemontaigu/pms-core';
import { bedConfigsLabel, outlookLabel, type OutlookKind } from './logic.js';

// Room pictograms for the Configuration table — the three glyph families that
// open every row: what you sleep in, what you look onto, how you wash.
//
// Drawn as inline SVG rather than an icon font or emoji: crisp at 20px,
// coloured from the stylesheet (one place to restyle), no dependency. Shapes
// stay literal — an abstract mark would need a legend, which defeats the point.

// ── Beds — plan view. Width AND colour carry the size ────────────────────────
//
// King 180 / double 160 / simple: three widths, three palettes, so a row reads
// at a glance without counting pillows. Sizes are the operator's (2026-08-06).

type BedSize = 'king' | 'double' | 'single';
const BED_WIDTH: Record<BedSize, number> = { king: 28, double: 24, single: 14 };

function Bed({ size }: { size: BedSize }) {
  const w = BED_WIDTH[size];
  const wide = size !== 'single';
  const pillow = (x: number) => <rect x={x} y={2.6} width={9} height={4.4} rx={1.8} className="pms-bed-pillow" />;
  return (
    <svg
      className={`pms-bed is-${size}`}
      width={w}
      height={20}
      viewBox={`0 0 ${w} 20`}
      aria-hidden="true"
      focusable="false"
    >
      <rect x={0.5} y={0.5} width={w - 1} height={19} rx={3} className="pms-bed-frame" />
      {wide ? (
        <>
          {pillow(2.2)}
          {pillow(w - 11.2)}
        </>
      ) : (
        pillow(2.5)
      )}
      <path
        d={`M1.6 9 h${w - 3.2} v7.9 a1.6 1.6 0 0 1 -1.6 1.6 h${-(w - 6.4)} a1.6 1.6 0 0 1 -1.6 -1.6 Z`}
        className="pms-bed-duvet"
      />
    </svg>
  );
}

/** One arrangement, drawn. The double renders as a KING when the ROOM holds
 *  one — 0173 moved that fact off the arrangement, because `Double` is
 *  satisfied by either bed. */
function BedRow({ layout, doubleBedType }: { layout: BedLayout; doubleBedType: string | null }) {
  const doubleSize = doubleBedType === 'King' ? 'king' : 'double';
  return (
    <>
      {Array.from({ length: layout.doubles }, (_, i) => (
        <Bed key={`d${i}`} size={doubleSize} />
      ))}
      {Array.from({ length: layout.singles }, (_, i) => (
        <Bed key={`s${i}`} size="single" />
      ))}
    </>
  );
}

/**
 * Every arrangement a room can be made up in, with the one it is STANDING in
 * marked when that is known.
 *
 * ⚠ It draws them ALL, rather than one plus a ⇄. The old column showed the
 * nominal layout and a little arrow meaning « converts to something »; what it
 * converted TO was only in the tooltip, and the code name (`Double - Twin`)
 * was the only place the pair was written down. Since 0173 the pair IS the
 * data, so the column can simply say it.
 *
 * `is-current` highlights the standing arrangement; when nothing is known, none
 * is highlighted — deliberately, because there is no default to fall back on
 * and pretending otherwise is what 0157 refused on the ménage sheet.
 */
export function BedIcons({
  layouts,
  doubleBedType = null,
  current = null,
}: {
  layouts: readonly { code: string; layout: BedLayout | null }[];
  doubleBedType?: string | null;
  current?: string | null;
}) {
  const known = layouts.filter(
    (l): l is { code: string; layout: BedLayout } => l.layout !== null,
  );
  if (known.length === 0) return <span className="pms-cfg-none">—</span>;

  const label = bedConfigsLabel(layouts, doubleBedType, current);
  const inferred = known.some((l) => l.layout.inferred);

  return (
    <span className={`pms-beds ${inferred ? 'is-inferred' : ''}`} title={label} aria-label={label}>
      {known.map((l, i) => (
        <span
          key={l.code}
          className={`pms-beds-opt ${l.code === current ? 'is-current' : ''}`}
        >
          {i > 0 && <span className="pms-bed-conv">⇄</span>}
          <BedRow layout={l.layout} doubleBedType={doubleBedType} />
        </span>
      ))}
    </span>
  );
}

// ── Outlook — balcony for a supérieure, otherwise what the room faces ────────

const OUTLOOK_GLYPH: Record<OutlookKind, ReactNode> = {
  // Balcony: railing seen head-on — top rail, balusters, floor.
  balcony: (
    <>
      <path d="M1 6 H19" className="pms-ico-stroke" />
      <path d="M4 6 v10 M8 6 v10 M12 6 v10 M16 6 v10" className="pms-ico-stroke is-thin" />
      <path d="M1 11 H19" className="pms-ico-stroke is-thin" />
      <path d="M0.6 16.6 H19.4" className="pms-ico-stroke" />
    </>
  ),
  // Castle: two crenellated towers flanking a keep, with a gate.
  castle: (
    <>
      <path d="M1.4 18 V5 h1.9 V3 h1.9 v2 h1.9 v13 Z" className="pms-ico-fill" />
      <path d="M12.9 18 V5 h1.9 V3 h1.9 v2 h1.9 v13 Z" className="pms-ico-fill" />
      <path d="M6.6 18 V8 h1.9 V6.4 h1.5 V8 h1.5 V6.4 h1.5 V8 h0.5 v10 Z" className="pms-ico-fill is-soft" />
      <path d="M8.9 18 v-4.6 a1.1 1.1 0 0 1 2.2 0 V18 Z" className="pms-ico-cut" />
    </>
  ),
  // Garden: a tree — full canopy over a trunk.
  garden: (
    <>
      <circle cx={10} cy={8} r={6} className="pms-ico-fill" />
      <circle cx={5} cy={11.4} r={3.8} className="pms-ico-fill is-soft" />
      <circle cx={15} cy={11.4} r={3.8} className="pms-ico-fill is-soft" />
      <path d="M9 18 v-6 h2 v6 Z" className="pms-ico-cut" />
      <path d="M10 18 V10.5" className="pms-ico-stroke is-thin" />
    </>
  ),
  // City: a skyline of three blocks, the tallest carrying windows.
  city: (
    <>
      <path d="M0.8 18 V8.6 h5.4 V18 Z" className="pms-ico-fill is-soft" />
      <path d="M6.9 18 V2.6 h6.2 V18 Z" className="pms-ico-fill" />
      <path d="M13.8 18 v-7.4 h5.4 V18 Z" className="pms-ico-fill is-soft" />
      <path d="M8.4 5.4 h1.6 v1.8 h-1.6 Z M11.2 5.4 h1.6 v1.8 h-1.6 Z M8.4 9 h1.6 v1.8 h-1.6 Z M11.2 9 h1.6 v1.8 h-1.6 Z M8.4 12.6 h1.6 v1.8 h-1.6 Z M11.2 12.6 h1.6 v1.8 h-1.6 Z" className="pms-ico-cut" />
    </>
  ),
  unknown: null,
};

export function OutlookIcon({
  kind,
  isSuperior,
  view,
}: {
  kind: OutlookKind;
  isSuperior: boolean;
  view: string | null;
}) {
  const glyph = OUTLOOK_GLYPH[kind];
  if (glyph === null) return <span className="pms-cfg-none">—</span>;
  const label = outlookLabel(isSuperior, view);
  return (
    <span className={`pms-ico is-${kind}`} title={label} aria-label={label}>
      <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        {glyph}
      </svg>
    </span>
  );
}

// ── WC — separate room, or sharing the bathroom ──────────────────────────────
//
// The same toilet at the same size in both cases; what differs is the room
// drawn around it (with a doorway). The frame IS the fact — « séparé » means
// the WC has its own room — so encoding it as presence/absence of walls needs
// no legend, and the two states can't be confused with the Bain column.

// Plan view, like the beds — cistern at the top, bowl below, seat cut out.
const TOILET = (
  <>
    <rect x={6.6} y={4.3} width={6.8} height={3} rx={1.1} className="pms-ico-fill" />
    <ellipse cx={10} cy={11.2} rx={3.5} ry={4.4} className="pms-ico-fill is-soft" />
    <ellipse cx={10} cy={11.4} rx={1.9} ry={2.8} className="pms-ico-cut" />
  </>
);

export function WcIcon({ notSeparated }: { notSeparated: boolean }) {
  const label = notSeparated ? 'WC non séparé (dans la salle de bain)' : 'WC séparé (pièce dédiée)';
  return (
    <span className={`pms-ico ${notSeparated ? 'is-wc-open' : 'is-wc'}`} title={label} aria-label={label}>
      <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        {!notSeparated && (
          // Walls with a doorway at the bottom — the room the WC sits in.
          <path
            d="M12.6 18.9 H17 a2 2 0 0 0 2 -2 V3.1 a2 2 0 0 0 -2 -2 H3 a2 2 0 0 0 -2 2 V16.9 a2 2 0 0 0 2 2 H7.4"
            className="pms-ico-stroke is-thin"
          />
        )}
        {TOILET}
      </svg>
    </span>
  );
}

// ── Bath — walk-in shower, or a tub (0059: has_shower false = baignoire) ─────

export function BathIcon({ hasShower }: { hasShower: boolean }) {
  const label = hasShower ? 'Douche à l’italienne' : 'Baignoire';
  return (
    <span className={`pms-ico ${hasShower ? 'is-shower' : 'is-bath'}`} title={label} aria-label={label}>
      <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        {hasShower ? (
          <>
            {/* Shower: head on its arm, water falling. */}
            <path d="M3.4 2 v3.4 a5.4 5.4 0 0 0 5.4 5.4" className="pms-ico-stroke" fill="none" />
            <path d="M9.4 10.8 a5.2 5.2 0 0 1 9.6 0 Z" className="pms-ico-fill" />
            <path d="M11.4 13.6 v2.2 M14.2 13.6 v4 M17 13.6 v2.2" className="pms-ico-stroke is-thin" />
          </>
        ) : (
          <>
            {/* Tub: tap above the rim, basin, feet. */}
            <path d="M3.6 3.2 a2.6 2.6 0 0 1 5.2 0" className="pms-ico-stroke" fill="none" />
            <path d="M1 9.2 h18 v3 a4 4 0 0 1 -4 4 h-10 a4 4 0 0 1 -4 -4 Z" className="pms-ico-fill" />
            <path d="M4 16.4 v2 M16 16.4 v2" className="pms-ico-stroke is-thin" />
          </>
        )}
      </svg>
    </span>
  );
}
